import React, { useEffect, useState } from "react";
import { Card, Spinner, Row, Col, Container } from "react-bootstrap";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";  // Importando para personalizar ícones, se necessário
import "leaflet/dist/leaflet.css";
import { fetchAddressByCep } from "../../Fetch/FetchAddress";

interface ClienteEndereco {
  BAIRRO: string;
  CEP: string;
  CIDADE: string;
  CNPJ: string;
  ENDERECO: string;
  ESTADO: string;
  ID_PEDIDO: string;
  NOME_FANTASIA: string;
  NR_PEDIDO: string;
  NUMERO: string;
  latitude?: number;
  longitude?: number;
}

interface Props {
  dadosClientes: { vendas_x_endereco: ClienteEndereco[] } | null;
  loading: boolean;
  error: string | null;
}

const MapaLocalizacaoClientes: React.FC<Props> = ({ dadosClientes, loading, error }) => {
  const [clientes, setClientes] = useState<ClienteEndereco[]>([]);
  const [loadingCep, setLoadingCep] = useState<boolean>(false);

  const cache: { [key: string]: any } = {}; // Cache para armazenar resultados

  const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  const fetchCoordinatesNominatim = (endereco: string, cidade: string, estado: string) => {
    const enderecoCompleto = `${endereco}, ${cidade}, ${estado}`;
    return fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(enderecoCompleto)}`
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.length === 0) {
          const cidadeEstado = `${cidade}, ${estado}`;
          return fetch(
            `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(cidadeEstado)}`
          )
            .then((response) => response.json())
            .then((data) => {
              if (data && data[0]) {
                return { latitude: parseFloat(data[0].lat), longitude: parseFloat(data[0].lon) };
              }
              return null;
            });
        } else {
          return { latitude: parseFloat(data[0].lat), longitude: parseFloat(data[0].lon) };
        }
      });
  };

  const atualizarEnderecoCliente = (cliente: ClienteEndereco): Promise<ClienteEndereco> => {
    setLoadingCep(true);
    return fetchAddressByCep(cliente.CEP)
      .then(async (enderecoCompleto) => {
        setLoadingCep(false);

        if (enderecoCompleto) {
          cliente.ENDERECO = enderecoCompleto.endereco;
          cliente.BAIRRO = enderecoCompleto.bairro;
          cliente.CIDADE = enderecoCompleto.cidade;
          cliente.ESTADO = enderecoCompleto.estado;

          // Verifica se as coordenadas estão em cache
          if (cache[cliente.CEP]) {
            cliente.latitude = cache[cliente.CEP].latitude;
            cliente.longitude = cache[cliente.CEP].longitude;
          } else {
            // Adicionando o delay antes de buscar as coordenadas
            await delay(5);  // Atraso de 500ms antes de buscar as coordenadas
            return fetchCoordinatesNominatim(cliente.ENDERECO, cliente.CIDADE, cliente.ESTADO).then(
              (coordenadas) => {
                if (coordenadas) {
                  cliente.latitude = coordenadas.latitude;
                  cliente.longitude = coordenadas.longitude;
                  cache[cliente.CEP] = coordenadas; // Armazena as coordenadas no cache
                }
                return cliente; // Garantir retorno do cliente atualizado
              }
            );
          }
        }
        return cliente; // Garantir que sempre retorne o cliente atualizado
      })
      .catch((error) => {
        console.error("Erro ao buscar endereço ou coordenadas:", error);
        setLoadingCep(false);
        return cliente; // Caso haja erro, retornamos o cliente original
      });
  };

  useEffect(() => {
    if (dadosClientes?.vendas_x_endereco) {
      setLoadingCep(true);

      // Usando delay para aguardar um tempo antes de fazer as requisições
      const atualizarClientes = async () => {
        await delay(10); // Atraso de 1 segundo antes de processar os clientes

        // Usando Promise.all para requisições paralelas
        Promise.all(
          dadosClientes.vendas_x_endereco.map((cliente) => atualizarEnderecoCliente(cliente))
        ).then((clientesAtualizados) => {
          setClientes(clientesAtualizados.filter(cliente => cliente !== null)); // Filtra qualquer cliente que tenha sido retornado como null ou inválido
          setLoadingCep(false);
        });
      };

      atualizarClientes();
    }
  }, [dadosClientes]);

  // Filtra clientes com coordenadas válidas
  const clientesComCoordenadas = clientes.filter(
    (cliente) => cliente.latitude !== undefined && cliente.longitude !== undefined
  );

  // Define um ícone personalizado para o marcador (opcional)
  const customIcon = new L.Icon({
    iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png", // URL do ícone
    iconSize: [25, 41], // Tamanho do ícone
    iconAnchor: [12, 41], // Ancoragem do ícone
    popupAnchor: [1, -34], // Posição do popup
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png", // URL da sombra do marcador
    shadowSize: [41, 41], // Tamanho da sombra
  });

  return (
    <Card className="p-4" style={{height: "800px"}}>
      <h5>Mapa de Localização dos Clientes</h5>
      {loading || loadingCep ? (
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p>Carregando dados...</p>
        </div>
      ) : error ? (
        <div className="text-center text-danger">
          <p>{error}</p>
        </div>
      ) : (
        <MapContainer
          center={
            clientesComCoordenadas.length > 0
              ? [clientesComCoordenadas[0].latitude!, clientesComCoordenadas[0].longitude!]
              : [-23.55052, -46.633308]
          }
          zoom={5}
          style={{ width: "100%", height: "800px", borderRadius: "10px", marginTop: "10px" }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          {clientesComCoordenadas.map((cliente, index) => (
            <Marker
              key={index}
              position={[cliente.latitude!, cliente.longitude!]}
              icon={customIcon} // Adicionando ícone personalizado
            >
              <Popup>
                <strong>{cliente.NOME_FANTASIA}</strong> <br />
                {cliente.ENDERECO}, {cliente.NUMERO} <br />
                {cliente.BAIRRO} - {cliente.CIDADE}/{cliente.ESTADO} <br />
                <strong>CEP:</strong> {cliente.CEP} <br />
                <strong>CNPJ:</strong> {cliente.CNPJ} <br />
                <strong>Pedido:</strong> {cliente.NR_PEDIDO} (ID: {cliente.ID_PEDIDO})
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      )}
    </Card>
  );
};

export default MapaLocalizacaoClientes;
